/** Import VUEX nécessaire pour le store */
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';

/** Types custom utilisé par le vuex */
import AuthentificationMethodEnum from '@/enums/AuthentificationMethodEnum';
import ConnectionMethod from '@/types/ConnectionMethod';

/** Import pour l'accès aux WebAPIs via axios  */
import { API_URL } from "@/App.settings";
import axios from "axios";

/** Emplacement du login oidc dans le session storage */
const SESSION_STORAGE_OIDC_LOGIN: string = "oidc_login";
const SESSION_STORAGE_OIDC_KEY: string = "oidc_key";

/** 
 * Gère la phase pré-authentification.
 * Socke le login ou la clé d'authorité en fonction du mode d'authentification (Portail SSO, Page de connexion)
 * À partir de ces informations, il récupère la méthode d'authentification et les paramètres nécessaires.
 */
@Module({
    namespaced: true
})
class AuthentificationStore extends VuexModule {

    /** Login en cours */
    private login!: string;

    /** Clé récupéré en paramètre lors de l'utilisation du portail OIDC */
    private authorityKey!: string;

    /** Méthode d'authentification en cours */
    private connectionMethod!: ConnectionMethod;

//#region Mutations
    /** Set la méthode de connection employé */
    @Mutation
    private setConnectionMethod(method: ConnectionMethod){
        this.connectionMethod = method;
    }

    /** Set le login */
    @Mutation
    private setLogin(login: string){
        this.login = login;
    }

    /** Set la clé de configuration oidc */
    @Mutation
    private setAuthorityKey(code: string){
        this.authorityKey = code;
    }
//#endregion Mutations

//#region Actions
    /** Charge le login depuis le "sessionStorage". */
    @Action({ rawError: true })
    private loadLogin(){
        if(!this.login){
            /** Est-ce sauvegardé en session storage ?*/
            const login: string = sessionStorage.getItem(SESSION_STORAGE_OIDC_LOGIN);
            if(login){
                this.context.commit('setLogin', login);
            }
        }
    }

    /** Charge la clé d'autorité depuis le "sessionStorage". */
    @Action({ rawError: true })
    private loadAuthorityKey(){
        if(!this.authorityKey){
            /** Est-ce sauvegardé en session storage ?*/
            const codePortail: string = sessionStorage.getItem(SESSION_STORAGE_OIDC_KEY);
            if(codePortail){
                this.context.commit('setAuthorityKey', codePortail);
            }
        }
    }

    /** Récupère la méthode d'authentification du login stocké dans les propriétés. */
    @Action({ rawError: true })
    private async loadConnectionMethod(){
        if(this.connectionMethod) /** Si il est est déjà chargé, nous n'avons pas besoin de le recharger */
            return; //END

        /** Phase recherche de configuration via l'authority key */
        if(!this.authorityKey)
            await this.context.dispatch('loadAuthorityKey');
        if(this.authorityKey){
            /** Récupère la méthode d'authentification auprès du serveur. */
            const response = await axios.post(API_URL + "Connexion/GetAuthMethod", { AuthorityKey: this.authorityKey })
            if (response && response.data) 
                this.context.commit('setConnectionMethod', response.data);
            return; //END
        }

        /** Phase recherche de configuration via le login */
        if(!this.login) 
            await this.context.dispatch('loadLogin');
        if(this.login){
            /** Récupère la méthode d'authentification auprès du serveur. */
            const response = await axios.post(API_URL + "Connexion/GetAuthMethod", { Login: this.login })
            if (response && response.data) 
                this.context.commit('setConnectionMethod', response.data);
            return; //END
        }

        /** Par défaut, nous considérons que c'est l'authentification basique qui fait foi. */
        this.context.commit('setConnectionMethod', { method: AuthentificationMethodEnum.Basic, oidcAuthority: undefined} )
    }

    /** 
     * Charge la méthode d'authentification via le "sessionStorage".
     * Si le store est déjà chargé alors le chargement est interrompu.
     * La réinitialisation est à la charge de l'utilisateur.
     */
    @Action({ rawError: true })
    public async Load(){
        if(!this.login && !this.authorityKey) /** détermine si le store est déjà initialisé */
            await this.context.dispatch('loadConnectionMethod'); /** Charge la methode de connection */
    }

    /** 
     * Charge la méthode d'authentification via le login.
     * Si le store est déjà chargé alors le nouveau login sera systématiquement ignoré.
     * La réinitialisation est à la charge de l'utilisateur.
     */
    @Action({ rawError: true })
    public async LoadFromLogin(login: string){
        if(!this.login && !this.authorityKey) { /** détermine si le store est déjà initialisé */
            if(login){
                sessionStorage.clear(); /** Vide le sessionStorage si l'on part pour une toute nouvelle session. */
                sessionStorage.setItem(SESSION_STORAGE_OIDC_LOGIN, login);
                this.context.commit('setLogin', login); /** Utilise le login en paramètre */
            }
            await this.context.dispatch('loadConnectionMethod'); /** Charge la methode de connection */
        }
        
    }

    /** 
     * Charge la méthode d'authentification via la clé d'autorité du portail OIDC.
     * Si le store est déjà chargé alors la nouvelle clé sera systématiquement ignoré..
     * La réinitialisation est à la charge de l'utilisateur.
     */
    @Action({ rawError: true })
    public async LoadFromKey(authorityKey: string){
        if(!this.login && !this.authorityKey) { /** détermine si le store est déjà initialisé */
            if(authorityKey){
                sessionStorage.clear(); /** Vide le sessionStorage si l'on part pour une toute nouvelle session. */
                sessionStorage.setItem(SESSION_STORAGE_OIDC_KEY, authorityKey);
                this.context.commit('setAuthorityKey', authorityKey); /** Utilise la clé d'autorité en paramètre */
            }
            await this.context.dispatch('loadConnectionMethod'); /** Charge la methode de connection */
        }
    }

    /** Réinitialise les variables locales (sessionStorage) utilisées par le store pour sauvegarder des états. */
    @Action({ rawError: true })
    public ResetStorage(){
        /** Purge le storage du login */
        if(sessionStorage.getItem(SESSION_STORAGE_OIDC_LOGIN))
            sessionStorage.removeItem(SESSION_STORAGE_OIDC_LOGIN);
        /** Purge le storage de la key */
        if(sessionStorage.getItem(SESSION_STORAGE_OIDC_KEY))
            sessionStorage.removeItem(SESSION_STORAGE_OIDC_KEY);
    }

    /** Réinitialise les propriétés du "store" */
    @Action({ rawError: true })
    public Reset(){
        /** Purge les propriétés du "store". */
        this.context.commit('setConnectionMethod', undefined);
        this.context.commit('setAuthorityKey', undefined);
        this.context.commit('setLogin', undefined);
    }
//#endregion Actions

//#region Getters
    /**
     * Récupère la méthode de connection chargé
     * @returns méthode de connection pour le login en cours
     */
    public get GetConnectionMethod(): ConnectionMethod{
        return this.connectionMethod;
    }

    /**
     * Récupère le login chargé
     */
    public get GetLogin(): string {
        return this.login;
    }
    
    /**
     * Récupère l'authority key chargé
     */
    public get GetAuthorityKey(): string {
        return this.authorityKey;
    }
//#endregion

};

export default AuthentificationStore;